/** Presentation helpers for the drive surface — no dependencies, i18n-agnostic. */

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  const digits = value >= 100 || exponent === 0 ? 0 : 1;
  return `${value.toFixed(digits)} ${BYTE_UNITS[exponent]}`;
}

export function formatTimestamp(ms: number, locale?: string): string {
  if (!ms) return '—';
  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(ms));
}

/** A coarse content type for the icon picker — the drive's own mimeType wins. */
export function entryIconKind(name: string, mimeType?: string, isFolder?: boolean): string {
  if (isFolder) return 'folder';
  const type = (mimeType || '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('text/')) return 'text';
  if (/zip|compressed|tar|rar|7z/.test(type)) return 'archive';
  if (/json|xml|javascript|csv|yaml/.test(type)) return 'code';
  const extension = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return 'image';
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(extension)) return 'video';
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(extension)) return 'audio';
  if (['md', 'txt', 'rtf', 'doc', 'docx'].includes(extension)) return 'text';
  if (['ts', 'tsx', 'js', 'jsx', 'py', 'rs', 'go', 'java', 'sh'].includes(extension)) return 'code';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'archive';
  return 'file';
}
